import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Mail, MapPin, ChevronDown } from "lucide-react";
import { useLang } from "@/context";
import { T, linkTo } from "@/i18n";
import { STEPS, TESTIMONIALS, FAQS, LEGAL_PAGES, TEAM } from "@/data/misc";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { LeadForm } from "@/components/LeadForm";
import { PropertyCard } from "@/components/PropertyCard";
import { CONTACT_EMAIL, CALENDAR_EMBED_URL } from "@/config";
import api from "@/api";

export function HowItWorks() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <div className="container-site py-28 md:py-36" data-testid="how-it-works-page">
      <SEO title={`${t.nav.howItWorks} — ${t.brand}`} description={t.home.stepsSub} lang={lang} pathKey="howItWorks" />
      <Reveal>
        <p className="eyebrow">{t.nav.howItWorks}</p>
        <h1 className="h2-serif mt-3">{t.home.stepsTitle}</h1>
        <p className="mt-4 text-[#1F2328]/70 max-w-2xl">{t.home.stepsSub}</p>
      </Reveal>
      <div className="mt-14 space-y-0">
        {STEPS[lang].map((s, i) => (
          <Reveal key={s.n} delay={i * 0.05}>
            <div className="grid md:grid-cols-12 gap-6 py-10 border-t border-[#0E4D5C]/10">
              <p className="font-serif text-6xl text-[#B5472B]/30 md:col-span-2">{s.n}</p>
              <div className="md:col-span-10">
                <h2 className="font-serif text-2xl sm:text-3xl text-[#0E4D5C]">{s.title}</h2>
                <p className="mt-3 text-[#1F2328]/70 leading-relaxed max-w-2xl">{s.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10"><Link to={linkTo(lang, "contact")} className="btn-primary" data-testid="hiw-cta">{t.nav.cta} <ArrowRight size={16} /></Link></Reveal>
    </div>
  );
}

export function About() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <div className="container-site py-28 md:py-36" data-testid="about-page">
      <SEO title={`${t.nav.about} — ${t.brand}`} description={t.footer.tagline} lang={lang} pathKey="about" />
      <Reveal>
        <p className="eyebrow">{t.nav.about}</p>
        <h1 className="h2-serif mt-3 max-w-3xl">{lang === "it" ? "Un ponte onesto tra l'Europa e il Madagascar" : "An honest bridge between Europe and Madagascar"}</h1>
        <div className="mt-6 max-w-3xl space-y-4 text-[#1F2328]/75 leading-relaxed">
          {lang === "it" ? (
            <>
              <p>Siamo nati da un'esperienza personale: anni di lavoro sul campo in Madagascar, tra titoli verificati uno a uno, notai, geometri e imprese locali. Abbiamo visto troppi stranieri perdere soldi per annunci civetta e promesse impossibili.</p>
              <p>Per questo ogni nostro annuncio ha una scheda di trasparenza: cosa abbiamo verificato, cosa no, quali rischi restano. Non promettiamo rendimenti garantiti — non esistono. Promettiamo metodo, onestà e presenza.</p>
              <p className="text-sm text-[#8A8F96]">[STORIA DEL FONDATORE — da completare con la storia reale del team]</p>
            </>
          ) : (
            <>
              <p>We were born from personal experience: years of field work in Madagascar, verifying titles one by one, working with notaries, surveyors and local builders. We have seen too many foreigners lose money to bait listings and impossible promises.</p>
              <p>That is why every listing of ours has a transparency sheet: what we verified, what we didn't, which risks remain. We don't promise guaranteed returns — they don't exist. We promise method, honesty and presence.</p>
              <p className="text-sm text-[#8A8F96]">[FOUNDER'S STORY — to be completed with the team's real story]</p>
            </>
          )}
        </div>
      </Reveal>
      <div className="mt-16 grid gap-8 sm:grid-cols-3">
        {TEAM.map((m, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="card-site p-7 text-center" data-testid={`team-member-${i}`}>
              <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#0E4D5C] text-[#F6EFE4] font-serif text-xl">{m.initials}</span>
              <p className="mt-4 font-medium text-[#1F2328]">{lang === "it" ? "[NOME E COGNOME]" : "[FULL NAME]"}</p>
              <p className="text-sm text-[#8A8F96] mt-1">{m.role[lang]}</p>
              <span className="mt-3 inline-block bg-[#E8B64C]/20 text-[#B5472B] text-[11px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1">{t.common.example}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function TestimonialsPage() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <div className="container-site py-28 md:py-36" data-testid="testimonials-page">
      <SEO title={`${t.nav.testimonials} — ${t.brand}`} description={t.home.testimonialsNote} lang={lang} pathKey="testimonials" />
      <Reveal>
        <p className="eyebrow">{t.nav.testimonials}</p>
        <h1 className="h2-serif mt-3">{t.home.testimonialsTitle}</h1>
        <p className="mt-4 text-sm text-[#8A8F96] max-w-2xl">{t.home.testimonialsNote}</p>
      </Reveal>
      <div className="mt-12 grid gap-8 md:grid-cols-3">
        {TESTIMONIALS.map((tm, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <figure className="card-site p-7 h-full">
              <blockquote className="text-sm text-[#1F2328]/75 leading-relaxed">{tm[lang]}</blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0E4D5C] text-[#F6EFE4] text-sm font-semibold">{tm.initials}</span>
                <span className="text-xs text-[#8A8F96]">{tm.meta[lang]}</span>
              </figcaption>
              <span className="mt-4 inline-block bg-[#E8B64C]/20 text-[#B5472B] text-[11px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1">{t.common.example}</span>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function FaqPage() {
  const { lang } = useLang();
  const t = T[lang];
  const [open, setOpen] = useState(0);
  return (
    <div className="container-site py-28 md:py-36 max-w-4xl" data-testid="faq-page">
      <SEO title={`${t.nav.faq} — ${t.brand}`} description={t.home.faqSub} lang={lang} pathKey="faq" />
      <Reveal>
        <p className="eyebrow">{t.nav.faq}</p>
        <h1 className="h2-serif mt-3">{t.home.faqTitle}</h1>
        <p className="mt-4 text-[#1F2328]/70">{t.home.faqSub}</p>
      </Reveal>
      <div className="mt-10 divide-y divide-[#0E4D5C]/10 border-y border-[#0E4D5C]/10">
        {FAQS.map((f, i) => (
          <div key={i}>
            <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-page-q-${i}`}
              className="w-full flex items-center justify-between gap-4 py-5 text-left font-serif text-lg text-[#1F2328] hover:text-[#B5472B] transition-colors" aria-expanded={open === i}>
              {f[lang].q}
              <ChevronDown size={18} className={`shrink-0 transition-transform duration-300 ${open === i ? "rotate-180 text-[#B5472B]" : ""}`} />
            </button>
            {open === i && <p className="pb-6 text-sm text-[#1F2328]/70 leading-relaxed max-w-3xl">{f[lang].a}</p>}
          </div>
        ))}
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f[lang].q, acceptedAnswer: { "@type": "Answer", text: f[lang].a } })),
      })}} />
    </div>
  );
}

export function ContactPage() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <div className="container-site py-28 md:py-36" data-testid="contact-page">
      <SEO title={`${t.nav.contact} — ${t.brand}`} description={t.property.contactSub} lang={lang} pathKey="contact" />
      <div className="grid lg:grid-cols-2 gap-14">
        <Reveal>
          <p className="eyebrow">{t.nav.contact}</p>
          <h1 className="h2-serif mt-3">{t.home.ctaTitle}</h1>
          <p className="mt-4 text-[#1F2328]/70 leading-relaxed">{t.home.ctaSub}</p>
          <div className="mt-8 space-y-4">
            <p className="flex items-center gap-3 text-sm text-[#1F2328]/75"><Mail size={17} className="text-[#B5472B]" /> {CONTACT_EMAIL}</p>
            <p className="flex items-center gap-3 text-sm text-[#1F2328]/75"><MapPin size={17} className="text-[#B5472B]" /> {t.footer.companyData}</p>
          </div>
          <div className="mt-10 card-site p-6" data-testid="calendar-embed-area">
            <p className="flex items-center gap-2 font-serif text-lg text-[#0E4D5C]"><CalendarDays size={18} /> {t.nav.cta}</p>
            {CALENDAR_EMBED_URL ? (
              <iframe src={CALENDAR_EMBED_URL} title="Booking" className="mt-4 w-full h-96 rounded-xl border-0" />
            ) : (
              <p className="mt-3 text-sm text-[#8A8F96] leading-relaxed">[SPAZIO PER CALENDARIO CAL.COM / CALENDLY — configurare l'URL di embed]</p>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="card-site p-7 md:p-9">
            <h2 className="font-serif text-2xl text-[#0E4D5C] mb-6">{t.property.contactTitle}</h2>
            <LeadForm testIdPrefix="contact-page-form" />
          </div>
        </Reveal>
      </div>
    </div>
  );
}

export function SellPage() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <div className="container-site py-28 md:py-36 max-w-3xl" data-testid="sell-page">
      <SEO title={`${t.nav.sell} — ${t.brand}`} description="" lang={lang} pathKey="sell" />
      <Reveal>
        <p className="eyebrow">{t.nav.sell}</p>
        <h1 className="h2-serif mt-3">{lang === "it" ? "Proponi il tuo immobile" : "List your property"}</h1>
        <p className="mt-4 text-[#1F2328]/70 leading-relaxed">
          {lang === "it"
            ? "Hai un terreno o un immobile in Madagascar? Lo valutiamo e, se supera le nostre verifiche, lo pubblichiamo con scheda di trasparenza. Raccontaci cosa hai: zona, superficie, stato del titolo."
            : "Do you own land or property in Madagascar? We evaluate it and, if it passes our checks, we publish it with a transparency sheet. Tell us what you have: area, surface, title status."}
        </p>
      </Reveal>
      <Reveal className="mt-10 card-site p-7 md:p-9">
        <LeadForm type="sell" testIdPrefix="sell-form" />
      </Reveal>
    </div>
  );
}

export function FreeGuidePage() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <div className="container-site py-28 md:py-36" data-testid="free-guide-page">
      <SEO title={`${t.home.guideTitle} — ${t.brand}`} description={t.home.guideSub} lang={lang} pathKey="freeGuide" />
      <div className="grid lg:grid-cols-2 gap-14 items-center max-w-5xl">
        <Reveal>
          <p className="eyebrow">{lang === "it" ? "Download gratuito" : "Free download"}</p>
          <h1 className="h2-serif mt-3">{t.home.guideTitle}</h1>
          <p className="mt-4 text-[#1F2328]/70 leading-relaxed">{t.home.guideSub}</p>
          <ul className="mt-6 space-y-2 text-sm text-[#1F2328]/70">
            {(lang === "it"
              ? ["Il mercato immobiliare malgascio spiegato bene", "Le vie legali per acquistare da stranieri", "Tutti i costi reali, voce per voce", "Gli errori più comuni — e come evitarli"]
              : ["The Malagasy property market, properly explained", "The legal routes for foreign buyers", "All real costs, line by line", "The most common mistakes — and how to avoid them"]
            ).map((li) => <li key={li} className="flex items-start gap-2"><ArrowRight size={14} className="text-[#B5472B] mt-1 shrink-0" /> {li}</li>)}
          </ul>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="card-site p-7 md:p-9">
            <LeadForm type="guide" compact testIdPrefix="free-guide-form" />
          </div>
        </Reveal>
      </div>
    </div>
  );
}

export function FarmlandPage() {
  const { lang } = useLang();
  const t = T[lang];
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/listings", { params: { type: "farmland" } }).then((r) => setItems(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="farmland-page">
      <SEO title={`${t.farmlandPage.title} — ${t.brand}`} description={t.farmlandPage.sub} lang={lang} pathKey="farmland" />
      <div className="container-site py-28 md:py-36">
        <Reveal>
          <p className="eyebrow">{t.nav.farmland}</p>
          <h1 className="h2-serif mt-3 max-w-3xl">{t.farmlandPage.title}</h1>
          <p className="mt-4 text-[#1F2328]/70 max-w-2xl leading-relaxed">{t.farmlandPage.sub}</p>
        </Reveal>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {t.farmlandPage.whyPoints.map((w, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="card-site p-7 h-full">
                <p className="font-serif text-4xl text-[#B5472B]/30">0{i + 1}</p>
                <h2 className="font-serif text-xl font-semibold mt-3 text-[#1F2328]">{w.t}</h2>
                <p className="mt-2 text-sm text-[#1F2328]/65 leading-relaxed">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        {items.length > 0 && (
          <div className="mt-16">
            <h2 className="h2-serif !text-3xl">{t.farmlandPage.cta}</h2>
            <div className="mt-8 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {items.map((item) => <PropertyCard key={item.ref_code} item={item} />)}
            </div>
          </div>
        )}
        <Reveal className="mt-14"><Link to={linkTo(lang, "contact")} className="btn-primary" data-testid="farmland-cta">{t.nav.cta} <ArrowRight size={16} /></Link></Reveal>
      </div>
    </div>
  );
}

export function LegalPage({ kind }) {
  const { lang } = useLang();
  const t = T[lang];
  const page = LEGAL_PAGES[kind][lang];
  return (
    <div className="container-site py-28 md:py-36 max-w-3xl" data-testid={`legal-${kind}-page`}>
      <SEO title={`${page.title} — ${t.brand}`} description={page.title} lang={lang} pathKey={kind} />
      <Reveal>
        <p className="eyebrow">{t.footer.legal}</p>
        <h1 className="h2-serif mt-3">{page.title}</h1>
      </Reveal>
      <div className="mt-10 space-y-8">
        {page.sections.map((s, i) => (
          <Reveal key={i}>
            <h2 className="font-serif text-xl font-semibold text-[#0E4D5C]">{s.h}</h2>
            <p className="mt-2 text-[#1F2328]/75 leading-relaxed">{s.p}</p>
          </Reveal>
        ))}
      </div>
      <p className="mt-12 rounded-xl bg-[#E8B64C]/15 border border-[#E8B64C]/40 p-4 text-sm text-[#1F2328]/70" data-testid="legal-note">
        {lang === "it" ? "Nota: testo da far verificare a un legale prima della pubblicazione." : "Note: text to be reviewed by a lawyer before publication."}
      </p>
    </div>
  );
}

export function NotFound() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <div className="container-site py-40 text-center" data-testid="not-found-page">
      <p className="font-serif text-8xl text-[#B5472B]/25">404</p>
      <h1 className="h2-serif mt-4">{t.notFound.title}</h1>
      <p className="mt-4 text-[#1F2328]/65 max-w-md mx-auto">{t.notFound.text}</p>
      <Link to={linkTo(lang, "home")} className="btn-primary mt-8" data-testid="not-found-home">{t.notFound.cta}</Link>
    </div>
  );
}
